// editableFields.js - Whitelist of editable text fields for site editor
//
// This config defines exactly which fields are editable in the site editor.
// Only text/copy should be editable - no structural changes, no add/remove items.
//
// Structure:
// - simpleFields: Array of field names that are simple strings (headline, subheadline, etc.)
// - arrayFields: Object where key = array name, value = array of editable field names within each item
//
// Example:
// {
//   hero: {
//     simpleFields: ['headline', 'subheadline'],
//     arrayFields: {}
//   },
//   testimonials: {
//     simpleFields: ['headline'],
//     arrayFields: {
//       items: ['quote', 'author', 'title']  // Edit these fields within each testimonial
//     }
//   }
// }

const ORISON_FIELDS = {
  hero: {
    simpleFields: ['headline', 'subheadline', 'buttonText', 'bottomText'],
    arrayFields: {}
  },
  problem: {
    simpleFields: [],
    arrayFields: {
      points: ['text']
    }
  },
  introduceSolution: {
    simpleFields: ['headline'],
    arrayFields: {
      paragraphs: ['text']
    }
  },
  benefits: {
    simpleFields: ['headline', 'subheadline'],
    arrayFields: {
      cards: ['title', 'description']
    }
  },
  threeStep: {
    simpleFields: [],
    arrayFields: {
      steps: ['heading', 'description']
    }
  },
  process: {
    simpleFields: ['headline', 'subheadline'],
    arrayFields: {
      steps: ['title', 'description']
    }
  },
  testimonials: {
    simpleFields: ['headline'],
    arrayFields: {
      items: ['quote', 'author', 'title', 'location', 'savings']
    }
  },
  faq: {
    simpleFields: ['headline'],
    arrayFields: {
      items: ['question', 'answer']
    }
  },
  footer: {
    simpleFields: ['headline', 'subheadline', 'buttonText', 'locationText', 'copyright'],
    arrayFields: {}
  }
};

const PARITY_FIELDS = {
  navbar: {
    simpleFields: ['ctaText', 'clientPortalLabel'],
    arrayFields: {
      navItems: ['label']
    }
  },
  hero: {
    simpleFields: ['headingLine1', 'subtitle', 'ctaPrimary', 'ctaSecondary'],
    arrayFields: {
      rotatingWords: []  // Flat string array - empty means edit the strings directly
    }
  },
  services: {
    simpleFields: ['heading', 'subheading', 'ctaHeading', 'ctaDescription', 'ctaButton'],
    arrayFields: {
      categories: ['title', 'subtitle', 'description']
    }
  },
  differentiation: {
    simpleFields: ['heading', 'subheading'],
    arrayFields: {
      cards: ['title', 'description']
    }
  },
  testimonials: {
    simpleFields: [],
    arrayFields: {},
    isTopLevelArray: true,
    topLevelArrayFields: ['quote', 'author', 'title']
  },
  footer: {
    simpleFields: ['description', 'phone', 'email', 'location'],
    arrayFields: {}
  }
};

const TEMPLATE_FIELDS = {
  orison: ORISON_FIELDS,
  harboroak: ORISON_FIELDS,
  parity: PARITY_FIELDS,
};

export function getFieldsForTemplate(templateType) {
  return TEMPLATE_FIELDS[templateType] || ORISON_FIELDS;
}

/**
 * Check if a field is editable
 * @param {string} section - Section name (e.g., 'hero', 'benefits')
 * @param {string} fieldName - Field name (e.g., 'headline', 'cards')
 * @param {string} templateType - Template type (e.g., 'orison', 'parity')
 * @returns {boolean | string[]} - false if not editable, array of editable subfields if it's an array field, true if it's a simple field
 */
export function isFieldEditable(section, fieldName, templateType) {
  const fields = getFieldsForTemplate(templateType);
  const sectionConfig = fields[section];

  if (!sectionConfig) {
    return false;
  }

  // Check if it's a simple field
  if (sectionConfig.simpleFields.includes(fieldName)) {
    return true;
  }

  // Check if it's an array field (returns the list of editable subfields)
  if (sectionConfig.arrayFields[fieldName]) {
    return sectionConfig.arrayFields[fieldName];
  }

  return false;
}

/**
 * Get all editable fields for a section
 * @param {string} section - Section name
 * @param {string} templateType - Template type (e.g., 'orison', 'parity')
 * @returns {object} - Object with simpleFields, arrayFields, and optional isTopLevelArray
 */
export function getEditableFieldsForSection(section, templateType) {
  const fields = getFieldsForTemplate(templateType);
  return fields[section] || { simpleFields: [], arrayFields: {} };
}
